import React, { useState } from "react";
import { Ear, Loader2, MessageCircle, PhoneCall, PhoneOff, Radio, Zap } from "lucide-react";
import { toast } from "sonner";
import { usePowerDialer } from "@/context/PowerDialerContext";
import { useCallDialog } from "@/context/CallDialogContext";
import { applyPreferredMicrophone } from "@/components/dialer/util/MicrophoneUtil";
import { DEVICE_PLATFORM } from "@/util/device";
import {
  useGetLiveMonitorCallsQuery,
  useGetMembersQuery,
  useStartMonitorMutation,
} from "@/store/rest_api";
import type { LiveMonitorEntry, MonitorMode } from "@/model/interface/Monitor";
import { apiErrorMessage } from "@/util/apiError";
import { memberName } from "@/util/member";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const MODE_LABELS: Record<MonitorMode, string> = {
  LISTEN: "Listening",
  WHISPER: "Whispering",
  BARGE: "Barged in",
};

function entryKey(entry: LiveMonitorEntry): string {
  return entry.campaignContactId ?? entry.callId ?? "";
}

export default function LiveMonitorPage() {
  const { registerAgentCall, agentVoiceCall } = usePowerDialer();
  const { device } = useCallDialog();
  const { data: liveCalls, error, isLoading } = useGetLiveMonitorCallsQuery(undefined, {
    pollingInterval: 5000,
  });
  const { data: members } = useGetMembersQuery({});
  const [startMonitor] = useStartMonitorMutation();
  const [activeMonitorKey, setActiveMonitorKey] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const agentNameFor = (userId?: string) => {
    if (!userId) {
      return "Unknown agent";
    }
    const membership = members?.find((member) => member.user?.id === userId);
    if (!membership?.user) {
      return "Unknown agent";
    }
    return memberName(membership, membership.user.email || "Unknown agent");
  };

  const callDurationFor = (entry: LiveMonitorEntry) => {
    if (!entry.connectedAt) {
      return "";
    }
    const seconds = Math.max(0, Math.floor((Date.now() - new Date(entry.connectedAt).getTime()) / 1000));
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
  };

  const handleMonitor = async (entry: LiveMonitorEntry, mode: MonitorMode) => {
    const id = entry.campaignContactId ?? entry.callId;
    if (!id) {
      return;
    }
    if (!device) {
      toast.error("Your browser phone isn't ready. Reload the page and try again.");
      return;
    }
    const key = entryKey(entry);
    setBusyKey(key);
    try {
      const session = await startMonitor({ source: entry.source, id, mode }).unwrap();
      await applyPreferredMicrophone(device);
      const monitorCall = await device.connect({ params: { monitorSession: session.id, platform: DEVICE_PLATFORM } });
      registerAgentCall(monitorCall);
      setActiveMonitorKey(key);
      toast.success(`${MODE_LABELS[mode]} on ${entry.contactName ?? entry.phoneNumber}`);
    } catch (monitorError) {
      toast.error(apiErrorMessage(monitorError, "Failed to join the call"));
    } finally {
      setBusyKey(null);
    }
  };

  const handleLeave = () => {
    agentVoiceCall?.disconnect();
    setActiveMonitorKey(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">
              {apiErrorMessage(error, "Live monitoring is available to organisation admins on the Pro plan.")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <Radio className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Live Monitor</h1>
          <p className="text-muted-foreground">Listen, whisper, or barge into live calls across your team</p>
        </div>
      </div>

      {activeMonitorKey && agentVoiceCall && (
        <div className="flex items-center justify-between rounded-lg border border-primary/40 bg-primary/10 px-4 py-3">
          <div className="flex items-center gap-2 text-sm">
            <PhoneCall className="h-4 w-4 text-primary" />
            <span className="font-medium">Monitoring a live call</span>
          </div>
          <Button size="sm" variant="destructive" onClick={handleLeave}>
            <PhoneOff className="h-3 w-3 mr-1.5" />
            Leave
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Live calls</CardTitle>
          <CardDescription>
            {liveCalls?.length
              ? `${liveCalls.length} conversation${liveCalls.length === 1 ? "" : "s"} in progress`
              : "No conversations in progress right now"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contact</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!liveCalls?.length && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    Live calls appear here while agents are connected
                  </TableCell>
                </TableRow>
              )}
              {liveCalls?.map((entry) => {
                const key = entryKey(entry);
                const busy = busyKey === key;
                return (
                  <TableRow key={key}>
                    <TableCell className="font-medium">
                      {entry.contactName ?? entry.phoneNumber ?? "Unknown"}
                    </TableCell>
                    <TableCell>
                      {entry.source === "POWER_DIALER" ? (
                        <Badge variant="outline" className="gap-1">
                          <Zap className="h-3 w-3" />
                          {entry.campaignName ?? "Power dialer"}
                        </Badge>
                      ) : (
                        <Badge variant="outline">{entry.direction === "INBOUND" ? "Inbound" : "Outbound"}</Badge>
                      )}
                    </TableCell>
                    <TableCell>{agentNameFor(entry.agentUserId)}</TableCell>
                    <TableCell>{callDurationFor(entry)}</TableCell>
                    <TableCell className="text-right space-x-2 whitespace-nowrap">
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => handleMonitor(entry, "LISTEN")}>
                        <Ear className="h-3 w-3 mr-1" />
                        Listen
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => handleMonitor(entry, "WHISPER")}>
                        <MessageCircle className="h-3 w-3 mr-1" />
                        Whisper
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => handleMonitor(entry, "BARGE")}>
                        <PhoneCall className="h-3 w-3 mr-1" />
                        Barge
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
